import { Fragment } from 'react';

import { useCartContext } from 'features/cart/hooks/use-cart-context';
import { useCartOfferValidation } from 'features/cart/hooks/use-cart-offer-validation';
import { commonStyles } from 'features/cart/styles';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { StyleSheet, Text, View } from 'react-native';
import { Icon } from 'ucl/components/icon/Icon';

import { Skeleton } from 'components/ui/skeleton';
import { t } from 'state/intl/translations';
import { componentsLibraryPrimitives, primitive } from 'styles/constants/primitives';

import { CartEntryDeleteButton } from '../cart-entry/cart-entry-action-buttons';

const FooterDiscountOfferSkeleton = () => {
  return (
    <View
      style={[
        styles.container,
        { backgroundColor: componentsLibraryPrimitives.$blackOpacity10, gap: 12 },
      ]}
    >
      <Skeleton shape="circle" style={{ width: 18, height: 18 }} />
      <View style={{ flexDirection: 'column', gap: 6, flex: 1 }}>
        <Skeleton shape="rectangle" style={{ width: '100%', height: 14, marginVertical: 0 }} />
        <Skeleton shape="rectangle" style={{ width: '60%', height: 12, marginVertical: 0 }} />
      </View>

      <Skeleton shape="rectangle" style={{ width: 80, height: 22, marginLeft: 10 }} />
    </View>
  );
};

const FooterDiscountOffer = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  const { feedbackMessages, isOfferApplied, isLoading } = useCartOfferValidation(cartEntry);

  const offerMessageKey = isOfferApplied ? 'offerApplied' : 'offerNotApplied';
  const offerMessage = t(offerMessageKey);
  const textSeparator = ' - ';

  let backgroundColor: string;
  if (!isLoading) {
    backgroundColor = isOfferApplied ? primitive.bk.$successLight : primitive.bk.$errorLight;
  } else {
    backgroundColor = componentsLibraryPrimitives.$blackOpacity10;
  }

  if (!cartEntry) {
    return null;
  }

  if (isLoading) {
    return <FooterDiscountOfferSkeleton />;
  }

  return (
    <View style={[styles.container, { backgroundColor }]}>
      <Icon marginRight={1} variant={isOfferApplied ? 'checkFill' : 'errorV2'} size="3" />
      <View style={{ flexDirection: 'column', gap: 2, flex: 1 }}>
        <Text style={commonStyles.cartEntryItemName}>{cartEntry.name}</Text>
        <Text style={styles.feedbackText}>
          {offerMessage}
          {feedbackMessages?.map((message, index) => (
            <Fragment key={index}>
              {textSeparator}
              {message}
            </Fragment>
          ))}
        </Text>
      </View>
      <CartEntryDeleteButton cartEntry={cartEntry} disabled={isLoading} variant="textOnly" />
    </View>
  );
};

export const CartFooterDiscountOffer = () => {
  const { cartEntries } = useCartContext();
  const cartEntry = cartEntries.find(entry => entry.type === MinimalCartEntryType.discount);

  if (!cartEntry) {
    return null;
  }

  return <FooterDiscountOffer cartEntry={cartEntry} />;
};

const styles = StyleSheet.create({
  container: {
    padding: 10,
    paddingHorizontal: 16,
    flex: 1,
    width: '100%',
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    borderRadius: 10,
  },
  feedbackText: {
    fontSize: 10,
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
  },
  removeText: {
    fontSize: 14,
    fontWeight: 'bold',
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.bk.$bbqBrown,
  },
});
